// Floating map panels: drag, edge-snap, collapse, close.
//
// The panels start where they always have — a column down the top-right — and
// only leave it once dragged. That is deliberate: first paint is identical to
// before, nothing has to be measured before the layout is right, and a visitor
// who never touches a panel never notices the feature exists.
//
// A dragged panel switches to `position: fixed` in viewport coordinates. #map
// is `position: absolute; inset: 0`, so viewport space and map space are the
// same rectangle, and leaving the flow lets the remaining panels close the gap.
//
// Pointer events rather than mouse events, so a finger or a stylus drags too.

/** Gutter a snapped panel keeps from the edge — the column's existing inset. */
export const EDGE_MARGIN = 16;

/**
 * How close an edge has to be before release snaps to it.
 *
 * From the design handoff. Wide enough that "put it back in the corner" is a
 * rough throw rather than a placement, narrow enough that a panel deliberately
 * parked near an edge stays where it was put.
 */
export const SNAP_THRESHOLD = 34;

/**
 * Below this width the panels are a scrollable strip at the top of the screen
 * (see the phone rules in style.css), which is a layout, not a canvas — there
 * is nowhere to drag to, and a drag would fight the scroll. Matches the 620px
 * breakpoint there.
 */
const DRAG_MIN_WIDTH = 621;

export interface Bounds { readonly width: number; readonly height: number; }

export interface Point { readonly x: number; readonly y: number; }

// ── The phone's one-at-a-time rule ──────────────────────────────────────────
//
// Below the breakpoint the panels are a strip with a fixed share of the screen,
// and every open box divides it. Four open at once is what the strip does today
// and it does not work: on a 6-inch Android with Trunk roads expanded, Weather
// got its title bar and none of its contents — a box that is present, named,
// and empty, which reads as a broken feed rather than as a full strip.
//
// So on a phone the console stops being four independent switches and becomes
// what the layout has claimed it was all along: a tab bar. One box is showing,
// tapping another swaps to it, tapping the showing one puts it away.
//
// The rule is applied on top of the stored state rather than written into it.
// That distinction is the whole reason this is two concepts instead of one: a
// panel is *dismissed* by its × and that is a decision worth keeping and worth
// syncing to the desktop, whereas a panel is *not the current tab* only because
// something else is, which is a fact about a narrow screen and must not follow
// the visitor back to a wide one. Folding the second into `hidden` would mean
// one look at the site on a phone silently closed three panels on the desktop.

/**
 * Whether a panel is off-screen, given what the visitor dismissed and which tab
 * a phone is showing.
 *
 * `solo` of null means the phone is showing no panel at all — the state after
 * tapping the lit console button, when someone wants the map and nothing else.
 */
export function panelHidden(
  dismissed: boolean, phone: boolean, solo: string | null, id: string,
): boolean {
  return dismissed || (phone && solo !== id);
}

/**
 * Which panel a phone opens on: the first one in console order that has not
 * been dismissed, or none if they have all been dismissed.
 */
export function firstShown(
  ids: readonly string[], dismissed: ReadonlySet<string>,
): string | null {
  return ids.find((id) => !dismissed.has(id)) ?? null;
}

/**
 * Keep a panel inside its container, leaving a margin on every side.
 *
 * A panel taller or wider than the container — a long Utilities list on a short
 * laptop screen — has no valid range at all, so it pins to the top-left margin
 * rather than being pushed off the other way by a negative maximum.
 */
export function clampToBounds(
  x: number, y: number, width: number, height: number, bounds: Bounds,
): Point {
  const maxX = bounds.width - width - EDGE_MARGIN;
  const maxY = bounds.height - height - EDGE_MARGIN;
  return {
    x: maxX < EDGE_MARGIN ? EDGE_MARGIN : Math.min(Math.max(x, EDGE_MARGIN), maxX),
    y: maxY < EDGE_MARGIN ? EDGE_MARGIN : Math.min(Math.max(y, EDGE_MARGIN), maxY),
  };
}

/**
 * Pull a released panel onto whichever edges it came to rest near.
 *
 * Horizontal and vertical are decided independently, so a panel dropped in a
 * corner snaps to both and lands square in it.
 */
export function snapToEdges(
  x: number, y: number, width: number, height: number, bounds: Bounds,
): Point {
  const c = clampToBounds(x, y, width, height, bounds);
  let { x: sx, y: sy } = c;

  if (sx - 0 <= SNAP_THRESHOLD) sx = EDGE_MARGIN;
  else if (bounds.width - (sx + width) <= SNAP_THRESHOLD) sx = bounds.width - width - EDGE_MARGIN;

  if (sy - 0 <= SNAP_THRESHOLD) sy = EDGE_MARGIN;
  else if (bounds.height - (sy + height) <= SNAP_THRESHOLD) sy = bounds.height - height - EDGE_MARGIN;

  // Snapping a panel bigger than its container would push it back out.
  return clampToBounds(sx, sy, width, height, bounds);
}

// ── Persisted state ─────────────────────────────────────────────────────────

interface PanelState {
  /** Viewport coordinates, meaningful only while `floating`. */
  x: number;
  y: number;
  /** True once dragged: the panel has left the top-right column for good. */
  floating: boolean;
  collapsed: boolean;
  hidden: boolean;
}

const STORAGE_KEY = 'abz.panels';
/** Pre-redesign key, read once so an existing visitor keeps what they hid. */
const LEGACY_HIDDEN_KEY = 'abz.hiddenBoxes';

/**
 * Panels that changed id, and the id their saved chrome moves to.
 *
 * The Utilities box became the Environment box — same panel, same place on
 * screen, new heading and a new dock glyph. Everything in storage is keyed by
 * id, so without this every visitor who had ever moved, collapsed or dismissed
 * that panel would find it back in its default position and visible again. The
 * layout would look lost rather than renamed, and nothing would have failed.
 *
 * Non-destructive and one-way: the old entry is copied, never deleted, and
 * only when the new id has nothing saved of its own. Somebody moving between
 * an old build and a new one keeps a working layout on both.
 */
const RENAMED: readonly (readonly [string, string])[] = [
  ['utilities', 'environment'],
];
/**
 * The phone's current tab, kept apart from the per-panel state above because it
 * is not a property of any one panel.
 *
 * Only a chosen tab is stored, never the closed-everything state. Someone who
 * put the last panel away and came back an hour later would have no memory of
 * having done it, and a site that opens with an empty console and a bare map
 * looks broken in a way that "it remembered exactly what I left" does not
 * repay. A reload is a fresh start; which tab you were reading is worth
 * keeping, having closed them all is not.
 */
const SOLO_KEY = 'abz.panelSolo';

function blank(): PanelState {
  return { x: 0, y: 0, floating: false, collapsed: false, hidden: false };
}

export interface PanelsOptions {
  /** Panel ids, matching the `box-<id>` element ids. */
  readonly ids: readonly string[];
  /** The element the panels live in, used for delegation. */
  readonly container: HTMLElement;
  /** The rectangle a panel may be dragged around. */
  readonly bounds: () => Bounds;
  /** Called whenever visibility changes, so the dock can redraw its state. */
  readonly onVisibilityChange?: () => void;
  /**
   * Panels this deployment has nothing to put in — the Events box in a city
   * with no events feed, which renders empty and is hidden by `.box:empty`.
   *
   * Only consulted when choosing the phone's opening tab, so that a strip does
   * not settle on a panel that will draw nothing. Everything else about an
   * absent panel already takes care of itself.
   */
  readonly absent?: (id: string) => boolean;
}

export interface Panels {
  /** Show or hide a panel. */
  setHidden(id: string, hidden: boolean): void;
  /** Flip a panel between hidden and shown — what the dock buttons do. */
  toggle(id: string): void;
  isHidden(id: string): boolean;
  /**
   * Re-apply every panel's chrome to the DOM. Panels re-render their own
   * innards, which replaces the header and its buttons, so this runs after
   * each render as well as on load.
   */
  apply(): void;
}

export function createPanels(opts: PanelsOptions): Panels {
  const state = new Map<string, PanelState>(opts.ids.map((id) => [id, blank()]));
  restore(state, opts.ids);

  /** The phone's current tab. Meaningless above the breakpoint. */
  let solo: string | null = restoreSolo(opts.ids);
  /**
   * Whether `solo` has been reconciled with what is actually on screen.
   *
   * Deferred until the first apply() at phone width, because that is the
   * earliest moment the answer is knowable and the latest it is needed — a
   * visitor who never narrows their window never has a tab at all.
   */
  let soloSettled = false;

  const get = (id: string): PanelState => state.get(id) ?? blank();

  /** Whether the one-at-a-time rule is in force at this width. */
  const phone = (): boolean => !draggable();

  /** Effective visibility: what was dismissed, plus what is not the open tab. */
  const hiddenNow = (id: string): boolean =>
    panelHidden(get(id).hidden, phone(), solo, id);

  /**
   * Settle on a tab the first time the strip is drawn.
   *
   * A remembered tab is kept unless it was dismissed in the meantime; otherwise
   * the first undismissed panel opens. Runs once, so that closing the last
   * panel leaves the console empty instead of instantly reopening another —
   * which is the difference between a tab bar and a thing that will not shut.
   */
  function settleSolo(): void {
    // Still showing something real: nothing to decide.
    if (solo !== null && !get(solo).hidden && !opts.absent?.(solo)) {
      soloSettled = true;
      return;
    }
    // Closed on purpose — the × on the last panel, or the lit console button.
    // Leave it closed; re-opening something here is what would make the console
    // feel like it will not shut.
    if (soloSettled && solo === null) return;

    // Either the first run, or the tab named a panel that turned out not to
    // exist here. The second case is real: the opening tab is settled during
    // the first paint, and which boxes this place can fill is only known once
    // /layers answers.
    soloSettled = true;
    solo = firstShown(
      opts.ids,
      new Set(opts.ids.filter((id) => get(id).hidden || opts.absent?.(id))),
    );
  }

  /** Open `id` as the phone's tab, whatever it was showing before. */
  function show(id: string): void {
    get(id).hidden = false;
    if (phone()) {
      soloSettled = true;
      solo = id;
    }
  }

  /** Put `id` away, and give up the tab if it held it. */
  function dismiss(id: string): void {
    get(id).hidden = true;
    if (solo === id) solo = null;
  }

  function save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(state)));
      if (solo !== null) localStorage.setItem(SOLO_KEY, solo);
    } catch {
      // Private browsing or a full quota. The panels still work; they forget.
    }
  }

  function element(id: string): HTMLElement | null {
    return document.getElementById(`box-${id}`);
  }

  /** Push one panel's state onto its element. */
  function applyOne(id: string): void {
    const el = element(id);
    if (!el) return;
    const s = get(id);

    const hidden = hiddenNow(id);
    el.classList.toggle('hidden', hidden);
    el.setAttribute('aria-hidden', String(hidden));
    el.classList.toggle('collapsed', s.collapsed);

    if (s.floating && draggable()) {
      // Re-clamp on every apply, not only on drag: a panel parked bottom-right
      // on a big screen would otherwise sit off-canvas after a resize or on the
      // next visit from a smaller window, unreachable and unclosable.
      const rect = el.getBoundingClientRect();
      const p = clampToBounds(s.x, s.y, rect.width, rect.height, opts.bounds());
      s.x = p.x;
      s.y = p.y;
      el.classList.add('floating');
      el.style.left = `${p.x}px`;
      el.style.top = `${p.y}px`;
    } else {
      // Phone layout, or never dragged: back into the column.
      el.classList.remove('floating');
      el.style.left = '';
      el.style.top = '';
    }

    const chevron = el.querySelector<HTMLButtonElement>('.box-collapse');
    if (chevron) {
      chevron.textContent = s.collapsed ? '▸' : '▾';
      chevron.setAttribute('aria-expanded', String(!s.collapsed));
    }
  }

  function apply(): void {
    if (phone()) settleSolo();
    for (const id of opts.ids) applyOne(id);
    opts.onVisibilityChange?.();
    save();
  }

  // ── Header controls ───────────────────────────────────────────────────────
  //
  // Delegated from the container rather than bound per render: renderTransport
  // and friends replace their whole innerHTML, which would drop any listener
  // attached to the buttons themselves.

  opts.container.addEventListener('click', (e) => {
    const button = (e.target as HTMLElement | null)?.closest<HTMLElement>('button');
    if (!button) return;

    const closeId = button.dataset.close;
    if (closeId) {
      dismiss(closeId);
      apply();
      return;
    }
    const collapseId = button.dataset.collapse;
    if (collapseId) {
      const s = get(collapseId);
      s.collapsed = !s.collapsed;
      apply();
    }
  });

  // ── Drag ──────────────────────────────────────────────────────────────────

  let drag: { id: string; el: HTMLElement; offsetX: number; offsetY: number } | null = null;

  opts.container.addEventListener('pointerdown', (e) => {
    if (!draggable()) return;
    // Secondary and middle buttons belong to the context menu and to autoscroll.
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const target = e.target as HTMLElement | null;
    const head = target?.closest<HTMLElement>('.box-head');
    // The chevron and the × live in the header and must stay clickable.
    if (!head || target?.closest('button')) return;

    const el = head.closest<HTMLElement>('.box');
    const id = el?.id.replace(/^box-/, '');
    if (!el || !id || !state.has(id)) return;

    const rect = el.getBoundingClientRect();
    const s = get(id);
    // Lift out of the column at exactly the pixels it already occupies, so the
    // panel does not jump on the first frame of the drag.
    s.floating = true;
    s.x = rect.left;
    s.y = rect.top;

    drag = { id, el, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
    el.classList.add('floating', 'dragging');
    el.style.left = `${rect.left}px`;
    el.style.top = `${rect.top}px`;

    // Capture on the header: the pointer routinely outruns a dragged element,
    // and without capture the move events land on the map underneath.
    head.setPointerCapture(e.pointerId);
    // Stops the drag turning into a text selection of the panel title.
    e.preventDefault();
  });

  opts.container.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const rect = drag.el.getBoundingClientRect();
    const p = clampToBounds(
      e.clientX - drag.offsetX, e.clientY - drag.offsetY,
      rect.width, rect.height, opts.bounds(),
    );
    drag.el.style.left = `${p.x}px`;
    drag.el.style.top = `${p.y}px`;
  });

  function endDrag(): void {
    if (!drag) return;
    const { id, el } = drag;
    drag = null;

    // Snap from where the panel actually is, not from the pointer: the two
    // differ by the grab offset, and snapping from the pointer would jump the
    // panel by that offset on every release.
    const rect = el.getBoundingClientRect();
    const s = get(id);
    const snapped = snapToEdges(rect.left, rect.top, rect.width, rect.height, opts.bounds());
    s.x = snapped.x;
    s.y = snapped.y;

    el.classList.remove('dragging');
    el.style.left = `${snapped.x}px`;
    el.style.top = `${snapped.y}px`;
    save();
  }

  opts.container.addEventListener('pointerup', endDrag);
  // A cancelled pointer (a system gesture, a browser back-swipe) must still put
  // the panel down, or it follows the next unrelated pointer move.
  opts.container.addEventListener('pointercancel', endDrag);

  // A window that shrinks past a floating panel would otherwise strand it. The
  // same handler covers crossing the phone breakpoint in either direction.
  window.addEventListener('resize', () => { apply(); });

  return {
    setHidden(id, hidden) {
      if (hidden) dismiss(id);
      else show(id);
      apply();
    },
    /**
     * The console button.
     *
     * Reads the effective state, so on a phone it acts on what is actually on
     * screen rather than on a panel the tab rule is already hiding.
     *
     * Closing the open tab on a phone clears the tab and stops there — it must
     * NOT dismiss. The two look identical on the phone and are not the same
     * thing at all: dismissal is stored per panel and shared with the desktop,
     * so routing the tab bar through it meant that flicking between Transport,
     * Roads and Weather on the way to work closed all three on the laptop at
     * home. That is the exact failure the split between `hidden` and `solo`
     * exists to prevent, and it was still reachable through here.
     */
    toggle(id) {
      if (hiddenNow(id)) show(id);
      else if (phone()) solo = null;
      else dismiss(id);
      apply();
    },
    isHidden: hiddenNow,
    apply,
  };
}

/** Whether this viewport gets the draggable canvas or the phone strip. */
function draggable(): boolean {
  return window.innerWidth >= DRAG_MIN_WIDTH;
}

/**
 * The remembered phone tab, if it still names a panel this place has.
 *
 * Checked against `ids` rather than trusted, for the reason the panel state
 * below is: storage outlives the code that wrote it, and a deployment without
 * an Events box must not open on a tab that does not exist — that would show
 * nothing at all and look exactly like a broken build.
 */
function restoreSolo(ids: readonly string[]): string | null {
  try {
    const raw = localStorage.getItem(SOLO_KEY);
    if (raw === null) return null;
    const saved = renamedTo(raw);
    return ids.includes(saved) ? saved : null;
  } catch {
    return null;
  }
}

/** An id's current name, following RENAMED. Unknown ids pass through. */
export function renamedTo(id: string): string {
  return RENAMED.find(([from]) => from === id)?.[1] ?? id;
}

/**
 * Load saved panel chrome, tolerating anything.
 *
 * Storage is shared with whatever else is on the origin and survives across
 * versions of this app, so every field is checked rather than trusted. Nothing
 * here is worth throwing over: the fallback is the default layout.
 */
function restore(state: Map<string, PanelState>, ids: readonly string[]): void {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return;
  }

  if (raw === null) {
    // First run since the redesign: carry over which panels were dismissed.
    try {
      const legacy = localStorage.getItem(LEGACY_HIDDEN_KEY);
      if (legacy) {
        for (const entry of JSON.parse(legacy) as unknown[]) {
          if (typeof entry !== 'string') continue;
          const id = renamedTo(entry);
          if (state.has(id)) state.get(id)!.hidden = true;
        }
      }
    } catch {
      // Nothing to carry over.
    }
    return;
  }

  try {
    const saved = JSON.parse(raw) as Record<string, Partial<PanelState>>;
    for (const [from, to] of RENAMED) {
      if (saved[from] && !saved[to]) saved[to] = saved[from]!;
    }
    for (const id of ids) {
      const s = saved[id];
      const target = state.get(id);
      if (!s || !target) continue;
      if (Number.isFinite(s.x)) target.x = Number(s.x);
      if (Number.isFinite(s.y)) target.y = Number(s.y);
      target.floating = s.floating === true;
      target.collapsed = s.collapsed === true;
      target.hidden = s.hidden === true;
    }
  } catch {
    // Corrupt entry; the defaults are already in place.
  }
}
